import queryString from 'query-string';
import { getDomain, getKrnDomain } from '@/common/api';
import { openUrl } from '@/common/bridge/openUrl';
import { getEnv } from '@/common/api';
import { localStorage } from '@kibt/krn-utils';
import getAbTestData from '@/common/abTest';
import { radar } from '@/common/log';
import yoda from '@kibt/bridge';
import { versionCompare } from '@kibt/web-utils/version';
import { appVersion } from '../device-info';
import { modifyUrlWithParams } from '../utils/modifyUrlWithParams';
import { useExtParamsObject } from '@/page/detail/views/content/utils';

export function useJumpToGoodsDetailPage() {
    const extParamsObj = useExtParamsObject();

    const jumpToGoodsDetailPage = (params: {
        itemId: string | number;
        sellerUserId?: string | number;
        merchantId?: string | number;
        pageType?: string | number;
        sellerSource?: string | number;
        extParams?: Record<string, any>;
        scene?: string;
        // 短视频凑单
        fromVideoAddon?: number;
        // 是否打开半屏
        halfWebview?: boolean;
        retrievalInfo?: Record<string, any>;
        // 后端下发跳转链接
        backEndDispatchUrl?: string;
    }) => {
        const {
            itemId,
            sellerUserId = '',
            merchantId = '',
            pageType = '',
            sellerSource = '',
            extParams,
            scene = '',
            fromVideoAddon,
            halfWebview = false,
            retrievalInfo,
            backEndDispatchUrl,
        } = params;
        const finalExtParams = Object.assign({}, extParamsObj, extParams);
        const extParamsEncoded = `${encodeURIComponent(JSON.stringify(finalExtParams))}`;
        const retrievalInfoEncoded = `${encodeURIComponent(JSON.stringify(retrievalInfo))}`;
        // 如果是达人分销商品，会带达人ID信息，需要透传  sellerUserId / merchantId
        const env = getEnv();
        const krnUrl =
            `bundleId=Detail&componentName=Detail&env=${env}&itemId=${itemId}&scene=${scene}&sellerUserId=${sellerUserId}&merchantId=${merchantId}&extParams=${extParamsEncoded}&fromVideoAddon=${fromVideoAddon}&retrievalInfo=${retrievalInfoEncoded}` +
            (pageType ? `&pageType=${pageType}` : '') +
            (sellerSource ? `&sellerSource=${sellerSource}` : '');
        const query = {
            itemId,
            scene,
            env,
            retrievalInfo: JSON.stringify(retrievalInfo),
            sellerUserId,
            merchantId,
            extParams: JSON.stringify(finalExtParams),
            fromVideoAddon,
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
            pageType,
            sellerSource,
        };
        const url = queryString.stringifyUrl(
            {
                url: `${getKrnDomain()}/krn-web/detail`,
                query,
            },
            { skipEmptyString: true, skipNull: true },
        );
        console.log('打开商品详情 halfWebview', halfWebview, ' 链接为：', url, extParamsEncoded);
        console.log('backEndDispatchUrl', backEndDispatchUrl);
        const backEndUrl = backEndDispatchUrl ? modifyUrlWithParams(backEndDispatchUrl, { ...query }, ['itemId', 'merchantId', 'redirectUrl']) : '';
        openUrl({ url, halfWebview }, { url: backEndUrl });
    };

    return { jumpToGoodsDetailPage };
}

export function useGetGoodsDetailPageUrl() {
    const extParamsObj = useExtParamsObject();
    const getGoodsDetailPageUrl = ({
        itemId,
        sellerUserId = '',
        merchantId = '',
        extParams,
        scene = '',
        fromVideoAddon,
        retrievalInfo,
    }: {
        itemId: string | number;
        sellerUserId?: string | number;
        merchantId?: string | number;
        extParams?: Record<string, any>;
        scene?: string;
        // 短视频凑单
        fromVideoAddon?: number;
        // 是否打开半屏
        halfWebview?: boolean;
        retrievalInfo?: Record<string, any>;
    }) => {
        const finalExtParams = Object.assign({}, extParamsObj, extParams);
        const extParamsEncoded = `${encodeURIComponent(JSON.stringify(finalExtParams))}`;
        const retrievalInfoEncoded = `${encodeURIComponent(JSON.stringify(retrievalInfo))}`;
        // 如果是达人分销商品，会带达人ID信息，需要透传  sellerUserId / merchantId
        const env = getEnv();
        const krnUrl = `bundleId=Detail&componentName=Detail&env=${env}&itemId=${itemId}&scene=${scene}&sellerUserId=${sellerUserId}&merchantId=${merchantId}&extParams=${extParamsEncoded}&fromVideoAddon=${fromVideoAddon}&retrievalInfo=${retrievalInfoEncoded}`;
        const url = `${getKrnDomain()}/krn-web/detail?itemId=${itemId}&scene=${scene}&retrievalInfo=${retrievalInfoEncoded}&sellerUserId=${sellerUserId}&merchantId=${merchantId}&env=${env}&extParams=${extParamsEncoded}&fromVideoAddon=${fromVideoAddon}&__launch_options__=${encodeURIComponent(
            JSON.stringify({
                topBarPosition: 'fixed',
            }),
        )}&redirectUrl=${encodeURIComponent(krnUrl)}`;
        return url;
    };
    return { getGoodsDetailPageUrl };
}

export function useJumpToConfirmOrder() {
    const extParamsObj = useExtParamsObject();
    // 跳转到确认下单页面
    // feat-url-dispatch 购物车跳提单
    const jumpToConfirmOrder = async (props: {
        fromMyShoppingCar?: number; // 注意：最开始只有购物车使用这个字段表示需要从localStorage获取购买信息，后来多个页面都使用这个字段来标识需要从localStorage获取购买信息，不仅仅是从购物车场景到提单页，故此字段没有具体的链路含义，仅仅表示需要从localStorage获取商品信息。
        addressId?: string | number;
        productItems?: {
            itemId: string | number;
            merchantId: string | number;
            skuId: string | number;
            sellerMerchantId?: string | number;
            num: number;
        }[];
        extParams?: {
            cId?: number;
            cT?: number;
            cS?: string;
        };
        backendDispatchUrl?: string;
    }) => {
        const { addressId, productItems, fromMyShoppingCar, backendDispatchUrl } = props;
        const extParams = Object.assign({}, extParamsObj, props?.extParams);
        const env = getEnv();
        if (fromMyShoppingCar) {
            await localStorage.setItem(
                'MyShoppingCarParams',
                JSON.stringify({
                    productItems,
                }),
            );
        }
        const krnUrl = queryString.stringify(
            {
                bundleId: 'EcommOrderConfirm',
                componentName: 'OrderConfirm',
                env,
                fromMyShoppingCar,
                // extParams: extParamsEncoded,
                extParams: JSON.stringify(extParams),
            },
            { sort: false },
        );
        const query = {
            referer_page: 'shopcar',
            addressId: addressId || '',
            fromMyShoppingCar,
            env,
            extParams: JSON.stringify(extParams),
            __launch_options__: JSON.stringify({
                enableLoading: false,
                topBarPosition: 'fixed',
            }),
            hyId: 'kibt-fe-shop-c-krn',
            redirectUrl: krnUrl,
        };
        const url = queryString.stringifyUrl(
            {
                url: `${getKrnDomain()}/krn-web/order-confirm`,
                query,
            },
            { skipEmptyString: true, skipNull: true },
        );
        console.log('jumpToConfirmOrder url::', url);
        const jumpUrl = backendDispatchUrl ? modifyUrlWithParams(backendDispatchUrl, { ...query }, ['merchantId', 'redirectUrl']) : '';

        return openUrl({ url, refererPage: 'MyShoppingCar' }, { url: jumpUrl });
    };

    return { jumpToConfirmOrder };
}

export function jumpToConfirmOrderWhenAddressAddDone(params: Record<string, any>) {
    const krnUrl = queryString.stringify(
        {
            bundleId: 'EcommOrderConfirm',
            componentName: 'OrderConfirm',
        },
        { sort: false },
    );
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/order-confirm`,
        query: {
            env: getEnv(),
            krnFollowParams: 1,
            redirectUrl: krnUrl,
            ...(params || {}),
        },
    });
    return openUrl({ url, refererPage: 'AddressAddDone' });
}

// 缓存店铺实验结果
let merchantKrnAbRes;
const getAbTestDataMerchant = async () => {
    if (merchantKrnAbRes === undefined) {
        merchantKrnAbRes = await getAbTestData({
            if_krn_merchant_fe: 'bool',
        });
        return merchantKrnAbRes;
    }
    return merchantKrnAbRes;
};

// 跳到店铺页面
// feat-url-dispatch 商详跳店铺
// feat-url-dispatch 小黄车跳跳店铺
// feat-url-dispatch 购物车跳店铺页
export const jumpToMerchant = async (params: {
    merchantId: string | number;
    isToBDetail?: boolean;
    extraInfoType?: string;
    type?: string;
    isPromotion?: string;
    authorId?: string;
    backendDispatchUrl?: string;
}) => {
    let path = '/shop/c/merchant?';
    const { merchantId, isToBDetail, extraInfoType, type, isPromotion, authorId, backendDispatchUrl } = params;
    if (isToBDetail) {
        path = '/shop/c/merchant?pageType=seller&';
    }
    if (isPromotion) {
        path = `/shop/c/merchant?isPromotion=${isPromotion}&`;
    }
    const urlOldH5 =
        getDomain() +
        `${path}hyId=kibt-fe-shop-c-v1&merchantId=${merchantId}&__launch_options__=${encodeURIComponent(
            JSON.stringify({
                enableLoading: false,
                topBarPosition: 'fixed',
            }),
        )}&extra_info=${encodeURIComponent(
            JSON.stringify({
                entrance_type_v2: type || '',
                entrance_type: extraInfoType || '',
            }),
        )}` +
        (authorId ? `&authorId=${authorId}` : '');
    const env = getEnv();
    let krnpath = '/shop/c/merchant?';
    if (isToBDetail) {
        krnpath = '/shop/c/merchant?pageType=seller&';
    }
    if (isPromotion) {
        krnpath = `/shop/c/merchant?isPromotion=${isPromotion}&`;
    }
    const krnUrl = `bundleId=EcommMerchant&componentName=Merchant&env=${env}&merchantId=${merchantId}&` + (authorId ? `authorId=${authorId}&` : '');
    const urlRN =
        `${getDomain()}${krnpath}merchantId=${merchantId}&env=${env}&__launch_options__=${encodeURIComponent(
            JSON.stringify({
                enableLoading: false,
                topBarPosition: 'fixed',
            }),
        )}&extra_info=${encodeURIComponent(
            JSON.stringify({
                entrance_type_v2: type || '',
                entrance_type: extraInfoType || '',
            }),
        )}&redirectUrl=${encodeURIComponent(krnUrl)}` + (authorId ? `&authorId=${authorId}` : '');

    let jumpUrl = urlOldH5;
    // const abResFromAPi = await getAbTestData({
    //     kwai_merchant_krn_fe: 'int',
    // });
    const abResFromAPi = await getAbTestDataMerchant();
    console.log('abResFromAPi ', abResFromAPi);
    if (abResFromAPi?.if_krn_merchant_fe?.value === true) {
        jumpUrl = urlRN;
    }
    radar.event({
        name: 'Shop_C_GetAbTestData_Merchant',
        params: {
            key: 'kwai_merchant_krn_fe',
            res: abResFromAPi,
        },
    });
    // console.log('Shop_KRN_GetAbTestData_Merchant', abResFromAPi);
    const backEndUrl = backendDispatchUrl
        ? modifyUrlWithParams(backendDispatchUrl, { ...queryString.parseUrl(jumpUrl).query }, ['itemId', 'merchantId', 'redirectUrl'])
        : '';
    openUrl({ url: jumpUrl }, { url: backEndUrl });
    console.log('jumpToMerchant');
};

const H5Base = '__launch_options__=%7B%22topBarPosition%22%3A%22fixed%22%7D&hyId=kibt-fe-shop-c-v1';

export function jumpToAddress({ addressId, entertSource, pageorigin }: { addressId: string | number; entertSource: number; pageorigin: string }) {
    const url = `${getDomain()}/shop/c/address?pageorigin=${pageorigin ?? ''}&addressIdInQuery=${addressId}&entertSource=${
        entertSource ?? 2
    }&formKrn=1&${H5Base}`;
    openUrl({ url });
}

// 跳转到新增地址
export const jumpToAddAddress = (props: {
    id?: string | number;
    type?: 'update';
    enterSource: string | number;
    pageorigin: string;
    enterAddressId?: string | number;
    isDefault: boolean;
}) => {
    const baseUrl = `${getDomain()}/shop/c/address/add?${H5Base}`;
    const url = queryString.stringifyUrl({
        url: baseUrl,
        query: {
            pageorigin: props?.pageorigin ?? '',
            enterSource: props?.enterSource ?? 2,
            formKrn: '1',
            type: props.type,
            id: props.id,
            enterAddressId: props?.enterAddressId,
            ...(props.isDefault ? { isDefault: 'TRUE' } : {}),
        },
    });
    console.log('jumpToAddAddress::', url);
    openUrl({ url });
};

// 商城TAB上线后，直接跳转TAB路由，其中url为无TAB兜底打开的H5
export const jumpToKwaiShop = ({ source }: { source: string }) => {
    // openUrl 中会强制修改source，这里加一个oSource代替原本的source
    const url = `${getDomain()}/shop/c/kwai-shop-new?isPrerenderPage=true&source=${source}&oSource=${source}&${H5Base}`;
    const res = versionCompare(appVersion, '9.11.30');
    if (res >= 0) {
        const schema = `ikwai://home/ecommerce?url=${encodeURIComponent(url)}`;
        (yoda.kwaiInvoke ?? yoda.invoke)?.('component.loadUri', {
            url: schema,
        });
    } else {
        openUrl({ url });
    }
    console.log('jumpToKwaiShop');
};

export const jumpToCodStatusPage = (receiptId: string | number) => {
    const url = `${getDomain()}/shop/c/pay-center/cod-status?receiptId=${receiptId}&${H5Base}`;
    openUrl({ url });
};

interface RefoundApplicationParams {
    type: string;
    orderId: string;
    mode?: string;
    subOrderId?: string;
    aftersaleQuantity?: number;
}
export const jumpToRefundApplication = (p: RefoundApplicationParams) => {
    const { type, orderId, mode, ...rest } = p;
    const url = queryString.stringifyUrl({
        url: `${getDomain()}/shop/c/aftersale/refund-application`,
        query: {
            ...rest,
            aftersaleType: type,
            orderId,
            mode,
            hyId: 'kibt-fe-shop-c-v1',
            __launch_options__: '%7B%22topBarPosition%22%3A%22fixed%22%7D',
        },
    });
    return openUrl({
        url,
    });
};

function getKsPayWallerDomain() {
    const runtimeEnv = getEnv();
    if (runtimeEnv === 'prod') return 'https://www.kwai-pay.com';
    if (runtimeEnv === 'prt') return 'https://kwai-pay-prt.test.gifshow.com';
    if (runtimeEnv === 'staging') return 'https://kspay-oversea-staging.test.gifshow.com';
    return 'https://www.kwai-pay.com';
}

// 跳转中台钱包页
export function openWalletPage(account_group_key = 'IDN_REFUND_ROLLBACK') {
    const url = `${getKsPayWallerDomain()}/page/kspay-oversea-wallet-withdraw-h5/v3/wallet?account_group_key=${account_group_key}`;
    return openUrl({ url });
}

// 没有被使用
/**
export const jumpToPoiPage = ({ poiId, extParams }: { poiId: string | number; extParams?: Record<string, any> }) => {
    const finalExtParams = Object.assign({}, getExtParams(), extParams);
    const extParamsEncoded = `${encodeURIComponent(JSON.stringify(finalExtParams))}`;
    const env = getEnv();
    const krnUrl = `bundleId=PoiDetail&componentName=PoiDetail&env=${env}&poiId=${poiId}&extParams=${extParamsEncoded}`;
    const url = `${getKrnDomain()}/krn-web/poi-detail?poiId=${poiId}&env=${env}&extParams=${extParamsEncoded}&__launch_options__=${encodeURIComponent(
        JSON.stringify({
            topBarPosition: 'fixed',
        }),
    )}&redirectUrl=${encodeURIComponent(krnUrl)}`;
    openUrl({ url });
};
*/

// 没有被使用
/**
export const jumpToKrnAddress = (payload?: {
    id?: string | number;
    type?: 'update';
    initialRouteName?: 'AddressHome' | 'AddressAdd';
    enterSource: string | number;
    pageorigin: string;
    enterAddressId?: string | number;
    [k: string]: string | number;
}) => {
    const krnUrl = queryString.stringify({
        bundleId: 'EcommAddress',
        componentName: 'EcommAddress',
        env: getEnv(),
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/address`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            extParams: getExtParams(),
            ...payload,
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
        },
    });
    console.log('address url:', url);
    return openUrl({ url });
};
 */

// 跳转到订单详情
export const jumpToOrderDetail = ({ orderId }) => {
    const krnUrl = queryString.stringify({
        bundleId: 'OrderDetail',
        componentName: 'OrderDetail',
        env: getEnv(),
        // orderId,
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/order-detail`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            // fromBundle: 'EcommOrderDetail',
            orderId,
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
        },
    });
    return openUrl({ url });
};

// 跳转新增评论页
export const jumpToAddComment = ({ orderId }) => {
    const krnUrl = queryString.stringify({
        bundleId: 'EcommAddComment',
        componentName: 'EcommAddComment',
        env: getEnv(),
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/add-comment`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            fromBundle: 'EcommOrderDetail',
            initialRouteName: 'CommentAdd',
            orderId,
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
        },
    });
    return openUrl({ url });
};

// 跳转评论页
export const jumpToComment = (payload: { itemId: string | number; scene?: string }) => {
    const krnUrl = queryString.stringify({
        bundleId: 'EcommComment',
        componentName: 'EcommComment',
        env: getEnv(),
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/comment`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            ...payload,
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
        },
    });
    return openUrl({ url });
};
